import { formatNumberWithDecimalPlaces } from "@/Utils/formatFunctions";
import { trpc } from "@/Utils/trpc";
import { BarChartView } from "@/components/v2/BarChartView";
import { defaultTimeFilter } from "@/components/v2/DatePicker/constants";

import { useContext, useMemo } from "react";

import { PortfolioPageContext } from "../../context";

export const PortfolioBarCharts: React.FC = () => {
  const { timeFilter, selectedBuildingIds } = useContext(PortfolioPageContext);

  const { data: terminalsData, isLoading } = trpc.dwh.terminals.get.useQuery(
    timeFilter || defaultTimeFilter,
    {
      queryKeyHashFn: () =>
        [
          "terminals",
          timeFilter?.startTimeISO,
          timeFilter?.endTimeISO,
        ].toString(),
      enabled: !!timeFilter,
    },
  );

  const filteredTerminals = useMemo(() => {
    if (!terminalsData) return [];
    return terminalsData.filter((t) =>
      selectedBuildingIds?.includes(t.terminalId),
    );
  }, [terminalsData, selectedBuildingIds]);

  const recyclingData = useMemo(() => {
    return filteredTerminals
      .map((t) => ({
        id: t.terminalId,
        label: t.terminalName,
        value: formatNumberWithDecimalPlaces(t.sortingDegree * 100, 1),
      }))
      .filter((t) => !!t.value)
      .sort((a, b) => b.value - a.value)
      .slice(0, 10)
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [filteredTerminals]);

  const nrWasteStreamsData = useMemo(() => {
    return filteredTerminals
      .map((t) => ({
        id: t.terminalId,
        label: t.terminalName,
        value: t.wasteTypes.length,
      }))
      .filter((t) => !!t.value)
      .sort((a, b) => b.value - a.value)
      .slice(0, 10)
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [filteredTerminals]);

  return (
    <div className="mt-6 flex flex-col min-[781px]:flex-row min-[781px]:justify-between">
      <div className="min-w-0 min-[781px]:w-1/2">
        <BarChartView
          title="portfolioPage:recyclingRatesTitle"
          subtitle="portfolioPage:recyclingRatesSubtitle"
          isLoading={isLoading}
          data={recyclingData}
          valueFormatter={(value) => `${value} %`}
        />
      </div>
      <div className="mt-6 min-w-0 min-[781px]:mt-0 min-[781px]:ml-4 min-[781px]:w-1/2">
        <BarChartView
          title="portfolioPage:wasteStreamsTitle"
          subtitle="portfolioPage:wasteStreamsSubtitle"
          isLoading={isLoading}
          data={nrWasteStreamsData}
        />
      </div>
    </div>
  );
};
